import React, { useDeferredValue, useMemo, useState } from 'react';
import {
  DndContext, DragEndEvent, DragOverEvent, KeyboardSensor, PointerSensor, closestCenter,
  useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Alert, Box, Button, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useInput, useRecordContext } from 'react-admin';
import { __t } from '../../../i18n/t';
import AddBlockDialog from './AddBlockDialog';
import BannerFormDialog from './BannerFormDialog';
import HomePreview from './HomePreview';
import RowContainer from './RowContainer';
import SortableRow from './SortableRow';
import {
  ChainModules, HomeBlock, HomeItem, HomeSectionDef, RowLayout, makeBlock, makeRow,
  mergeWithCatalog, resolveSectionCatalog, rowCapacity,
} from './homeItems';

interface HomeItemsInputProps {
  source?: string;
  /** Flags de módulo da rede: decidem quais destinos o bloco pode abrir. */
  chain?: ChainModules | null;
}

interface EditingTarget { rowId: string; block: HomeBlock }

const HomeItemsInput: React.FC<HomeItemsInputProps> = ({
  source = 'appHomeItems',
  chain,
}) => {
  const record = useRecordContext();
  const { field } = useInput({ source });

  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<EditingTarget | null>(null);

  const catalog: HomeSectionDef[] = useMemo(
    () => resolveSectionCatalog(record?.availableHomeSections as HomeSectionDef[] | undefined),
    [record?.availableHomeSections],
  );

  // O merge roda na leitura, não no save: seção lançada depois de alguém já ter
  // configurado precisa aparecer na lista, senão não há como ordená-la.
  const items: HomeItem[] = useMemo(
    () => mergeWithCatalog(field.value as HomeItem[] | undefined, catalog),
    [field.value, catalog],
  );

  const sectionById = useMemo(
    () => new Map(catalog.map(section => [section.id, section])),
    [catalog],
  );

  // Digitar no título de uma LINHA grava no formulário a cada tecla. Em prioridade
  // baixa, o React descarta a renderização da prévia interrompida pela tecla
  // seguinte: ela redesenha quando o operador para, em vez de a cada caractere.
  const previewItems = useDeferredValue(items);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const commit = (next: HomeItem[]) => field.onChange(next);

  const findRowOfBlock = (list: HomeItem[], blockId: string) =>
    list.find(item => item.kind === 'row' && (item.blocks || []).some(b => b.id === blockId));

  /**
   * Move um bloco para outra linha enquanto se arrasta. Roda no onDragOver (e não no
   * onDragEnd) para a linha de destino já mostrar o bloco durante o arrasto — sem isso
   * o operador solta no escuro.
   */
  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over || active.data.current?.type !== 'block') { return; }

    const from = findRowOfBlock(items, String(active.id));
    if (!from) { return; }

    const overId = String(over.id);
    const target = over.data.current?.type === 'row-drop'
      ? items.find(item => item.id === over.data.current?.rowId)
      : findRowOfBlock(items, overId) || items.find(item => item.id === overId && item.kind === 'row');
    if (!target || target.id === from.id) { return; }
    if ((target.blocks || []).length >= rowCapacity(target)) { return; }

    const block = (from.blocks || []).find(b => b.id === active.id);
    if (!block) { return; }

    commit(items.map(item => {
      if (item.id === from.id) { return { ...item, blocks: (item.blocks || []).filter(b => b.id !== block.id) }; }
      if (item.id === target.id) { return { ...item, blocks: [...(item.blocks || []), block] }; }
      return item;
    }));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) { return; }

    // Reordenar os dois blocos da mesma linha.
    if (active.data.current?.type === 'block') {
      const row = findRowOfBlock(items, String(active.id));
      if (!row) { return; }
      const blocks = row.blocks || [];
      const from = blocks.findIndex(b => b.id === active.id);
      const to = blocks.findIndex(b => b.id === over.id);
      if (from < 0 || to < 0 || from === to) { return; }
      commit(items.map(item => (
        item.id === row.id ? { ...item, blocks: arrayMove(blocks, from, to) } : item
      )));
      return;
    }

    const from = items.findIndex(item => item.id === active.id);
    const to = items.findIndex(item => item.id === over.id);
    if (from < 0 || to < 0) { return; }
    commit(arrayMove(items, from, to));
  };

  const replaceAt = (index: number, item: HomeItem) =>
    commit(items.map((current, position) => (position === index ? item : current)));

  const handlePickLayout = (layout: RowLayout) => {
    setAdding(false);
    const row = makeRow(layout);
    commit([...items, row]);
    setEditing({ rowId: row.id, block: row.blocks![0] });
  };

  const handleAddBlockToRow = (rowId: string) => {
    const row = items.find(item => item.id === rowId);
    if (!row || (row.blocks || []).length >= rowCapacity(row)) { return; }
    const block = makeBlock();
    commit(items.map(item => (
      item.id === rowId ? { ...item, blocks: [...(item.blocks || []), block] } : item
    )));
    setEditing({ rowId, block });
  };

  const removeBlock = (rowId: string, blockId: string) => commit(items.map(item => (
    item.id === rowId ? { ...item, blocks: (item.blocks || []).filter(b => b.id !== blockId) } : item
  )));

  const handleSaveBlock = (saved: HomeBlock) => {
    if (!editing) { return; }
    commit(items.map(item => (
      item.id === editing.rowId
        ? { ...item, blocks: (item.blocks || []).map(b => (b.id === saved.id ? saved : b)) }
        : item
    )));
    setEditing(null);
  };

  const handleCloseForm = () => {
    // Bloco recém-criado que nunca ganhou imagem não fica sujando a linha; e a linha
    // que ficou sem nenhum bloco também sai.
    if (editing && !editing.block.imagePath) {
      const row = items.find(item => item.id === editing.rowId);
      const current = (row?.blocks || []).find(b => b.id === editing.block.id);
      if (current && !current.imagePath) {
        const next = items
          .map(item => (item.id === editing.rowId
            ? { ...item, blocks: (item.blocks || []).filter(b => b.id !== current.id) }
            : item))
          .filter(item => item.kind !== 'row' || (item.blocks || []).length > 0);
        commit(next);
      }
    }
    setEditing(null);
  };

  const editingRow = editing ? items.find(item => item.id === editing.rowId) : undefined;
  const emptyBlocks = items.reduce(
    (total, item) => total + (item.blocks || []).filter(b => !b.imagePath).length, 0,
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, width: '100%' }}>
      <Box sx={{ flex: 1, minWidth: 0, maxWidth: 760 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Arraste para mudar a ordem das seções na Home do app, e use o lápis para renomear o título de cada uma. É necessário reabrir o App para visualizar as alterações.
        </Typography>

        {emptyBlocks > 0 && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            Há bloco de imagem sem arte escolhida. Ele não aparece no app até receber uma imagem.
          </Alert>
        )}

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={items.map(item => item.id)} strategy={verticalListSortingStrategy}>
            {items.map((item, index) => (
              item.kind === 'row' ? (
                <RowContainer
                  key={item.id}
                  row={item}
                  onToggleVisible={() => replaceAt(index, { ...item, visible: !item.visible })}
                  onChangeTitle={title => replaceAt(index, { ...item, title })}
                  onRemoveRow={() => commit(items.filter(current => current.id !== item.id))}
                  onEditBlock={blockId => {
                    const block = (item.blocks || []).find(b => b.id === blockId);
                    if (block) { setEditing({ rowId: item.id, block }); }
                  }}
                  onRemoveBlock={blockId => removeBlock(item.id, blockId)}
                  onAddBlock={() => handleAddBlockToRow(item.id)}
                />
              ) : (
                <SortableRow
                  key={item.id}
                  item={item}
                  sectionLabel={sectionById.get(item.id)?.label || item.id}
                  defaultTitle={sectionById.get(item.id)?.defaultTitle}
                  onToggleVisible={() => replaceAt(index, { ...item, visible: !item.visible })}
                  onChangeTitle={title => replaceAt(index, { ...item, title })}
                />
              )
            ))}
          </SortableContext>
        </DndContext>

        <Button startIcon={<AddIcon />} onClick={() => setAdding(true)} sx={{ mt: 1 }}>
          Adicionar bloco
        </Button>

        <AddBlockDialog open={adding} onClose={() => setAdding(false)} onPick={handlePickLayout} />
        <BannerFormDialog
          block={editing?.block || null}
          layout={editingRow?.layout || 'full'}
          chain={chain}
          onClose={handleCloseForm}
          onSave={handleSaveBlock}
        />
      </Box>

      {/* Acompanha a rolagem da lista; some em tela estreita, onde espremeria o editor. */}
      <Box sx={{ display: { xs: 'none', lg: 'block' }, position: 'sticky', top: 16, flexShrink: 0 }}>
        <HomePreview items={previewItems} sectionById={sectionById} />
      </Box>
    </Box>
  );
};

export default HomeItemsInput;
